import { useState, useEffect, useCallback } from 'react';
import { Star, MessageSquare, ChevronLeft, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import ConsumerLayout from '../../components/consumer/ConsumerLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import ConsumerReviewDrawer from '../../components/consumer/ConsumerReviewDrawer';
import Toast from '../../components/common/Toast';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const StarRow = ({ value, size = 12 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        className={i <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'fill-slate-100 text-slate-300'}
      />
    ))}
  </span>
);

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const truncate = (str, max = 80) => {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
};

const StatusBadge = ({ isApproved }) =>
  isApproved ? (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 whitespace-nowrap">
      ✓ Approved
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 whitespace-nowrap">
      ◷ Pending
    </span>
  );

const SkeletonRow = () => (
  <tr className="border-b border-slate-100">
    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3.5 bg-slate-200 rounded animate-pulse" style={{ width: i === 4 ? '60%' : '85%' }} />
      </td>
    ))}
  </tr>
);

/* ─────────────────────────────────────────────
   Page component
───────────────────────────────────────────── */
const ConsumerReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [pagination, setPagination] = useState({ limit: 20, offset: 0, total: 0, hasMore: false });

  // Filters & sort
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('date_desc'); // composite: sort_date_order
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('view'); // 'view' | 'edit'
  const [selectedReview, setSelectedReview] = useState(null);

  // Inline delete confirmation
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── Fetch ── */
  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const [sort_by, order] = sortKey.split('_');
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        sort_by,
        order,
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const res = await fetch(`${API_BASE}/consumer/reviews?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load reviews');
      const data = await res.json();
      setReviews(data.data || []);
      setPagination(data.pagination || { limit: itemsPerPage, offset: 0, total: 0, hasMore: false });
    } catch {
      showToast('Failed to load reviews', 'error');
    } finally {
      setReviewsLoading(false);
    }
  }, [currentPage, statusFilter, sortKey]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [statusFilter, sortKey]);

  /* ── Drawer handlers ── */
  const openViewDrawer = (review) => {
    setSelectedReview(review);
    setDrawerMode('view');
    setDrawerOpen(true);
  };

  const openEditDrawer = (review, e) => {
    e.stopPropagation();
    setSelectedReview(review);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedReview(null);
  };

  /* ── Delete handlers ── */
  const handleDeleteConfirm = (reviewId, e) => {
    e.stopPropagation();
    setDeletingId(reviewId);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/consumer/reviews/${deletingId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.message || 'Failed to delete review');
      }
      setDeletingId(null);
      showToast('Review deleted successfully');
      fetchReviews();
    } catch (err) {
      showToast(err.message || 'Failed to delete review', 'error');
    } finally {
      setDeleting(false);
    }
  };

  /* ── Stats computed from loaded page ── */
  const approvedCount = reviews.filter((r) => r.is_approved).length;
  const pendingCount = reviews.filter((r) => !r.is_approved).length;
  const avgRating =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
      : null;
  const totalHelpful = reviews.reduce((s, r) => s + (r.helpful_count || 0), 0);

  const totalPages = Math.max(1, Math.ceil(pagination.total / itemsPerPage));

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/consumer/dashboard' },
    { label: 'My Reviews' },
  ];

  return (
    <ConsumerLayout title="My Reviews">
      <div className="space-y-5">

        {/* ── Breadcrumb & Page header ── */}
        <div>
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-start justify-between gap-4 mt-1">
            <div>
              <h1 className="text-[22px] font-bold text-slate-900 leading-tight">My Reviews</h1>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="text-[12px] text-slate-400">
                  {reviewsLoading ? '—' : pagination.total} review{pagination.total !== 1 ? 's' : ''} written
                </span>
                {!reviewsLoading && avgRating && (
                  <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-700">
                    <StarRow value={parseFloat(avgRating)} size={13} />
                    {avgRating} avg
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats bar ── */}
        <div className="grid grid-cols-4 gap-3">
          <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Total Reviews</p>
            <p className="text-[22px] font-bold text-slate-900 mt-1">
              {reviewsLoading ? <span className="inline-block h-6 w-10 bg-slate-200 rounded animate-pulse" /> : pagination.total}
            </p>
          </div>
          <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Avg Rating</p>
            <p className="text-[22px] font-bold text-amber-600 mt-1">
              {reviewsLoading ? <span className="inline-block h-6 w-10 bg-slate-200 rounded animate-pulse" /> : (avgRating ?? '—')}
            </p>
          </div>
          <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Approved</p>
            <p className="text-[22px] font-bold text-emerald-600 mt-1">
              {reviewsLoading ? <span className="inline-block h-6 w-10 bg-slate-200 rounded animate-pulse" /> : approvedCount}
            </p>
          </div>
          {/* <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Helpful Votes</p>
            <p className="text-[22px] font-bold text-blue-600 mt-1">
              {reviewsLoading ? <span className="inline-block h-6 w-10 bg-slate-200 rounded animate-pulse" /> : totalHelpful}
            </p>
          </div> */}
        </div>

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
          >
            <option value="all">Status: All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
          </select>

          {/* Sort */}
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
          >
            <option value="date_desc">Sort: Newest first</option>
            <option value="date_asc">Sort: Oldest first</option>
            <option value="rating_desc">Sort: Rating high–low</option>
            <option value="rating_asc">Sort: Rating low–high</option>
          </select>

          <span className="ml-auto text-[11px] text-slate-400">
            {reviewsLoading ? '—' : pagination.total} result{pagination.total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── Table ── */}
        <div className="overflow-hidden border border-slate-200 rounded-lg bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Business</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[95px]">Rating</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[170px]">Title</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Content</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[105px]">Date</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[90px]">Status</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[120px]">Actions</th>
                </tr>
              </thead>

              <tbody>
                {reviewsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)

                ) : reviews.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <MessageSquare size={32} className="text-slate-300" />
                        <p className="text-[13px] font-medium text-slate-500">No reviews found</p>
                        <p className="text-[12px] text-slate-400">
                          {statusFilter !== 'all'
                            ? 'Try clearing the filters'
                            : 'Explore businesses and share your experience'}
                        </p>
                        {statusFilter !== 'all' && (
                          <button
                            onClick={() => setStatusFilter('all')}
                            className="mt-2 text-[12px] text-blue-600 hover:underline"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                ) : (
                  reviews.map((review, idx) => {
                    const isConfirmingDelete = deletingId === review.id;
                    return (
                      <tr
                        key={review.id}
                        onClick={() => !isConfirmingDelete && openViewDrawer(review)}
                        className={`border-b border-slate-100 hover:bg-slate-50/60 transition-colors cursor-pointer
                          ${idx % 2 === 0 ? '' : 'bg-slate-50/30'}`}
                      >
                        {/* Business */}
                        <td className="px-4 py-3">
                          <p className="text-[12px] font-medium text-slate-900 truncate max-w-[150px]">
                            {review.business?.business_name || '—'}
                          </p>
                          {review.business?.city && (
                            <p className="text-[10px] text-slate-400 truncate">{review.business.city}</p>
                          )}
                        </td>

                        {/* Rating */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5">
                            <StarRow value={review.rating} size={12} />
                            <span className="text-[10px] text-slate-500">{review.rating} / 5</span>
                          </div>
                        </td>

                        {/* Title */}
                        <td className="px-4 py-3">
                          <span
                            className="text-[12px] text-slate-700"
                            title={review.title}
                          >
                            {truncate(review.title, 55)}
                          </span>
                        </td>

                        {/* Content */}
                        <td className="px-4 py-3">
                          <span className="text-[12px] text-slate-500">
                            {truncate(review.content, 75)}
                          </span>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-[12px] text-slate-500">{formatDate(review.created_at)}</span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <StatusBadge isApproved={review.is_approved} />
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {isConfirmingDelete ? (
                            /* Inline confirmation */
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-red-600 font-medium mr-0.5">Delete?</span>
                              <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="px-2 py-1 text-[10px] font-semibold text-white bg-red-500 rounded hover:bg-red-600 disabled:opacity-60 transition-colors"
                              >
                                {deleting ? '…' : 'Yes'}
                              </button>
                              <button
                                onClick={() => setDeletingId(null)}
                                className="px-2 py-1 text-[10px] font-medium text-slate-500 border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              {!review.is_approved ? <button
                                onClick={(e) => openEditDrawer(review, e)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                                title="Edit this review"
                              >
                                <Pencil size={10} />
                                Edit
                              </button>:<></>}
                              <button
                                onClick={(e) => handleDeleteConfirm(review.id, e)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                                title="Delete this review"
                              >
                                <Trash2 size={10} />
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Pagination ── */}
        {pagination.total > itemsPerPage && (
          <div className="flex items-center justify-between text-[12px] text-slate-500">
            <span>
              Showing{' '}
              {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, pagination.total)}{' '}
              of {pagination.total}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="px-3 py-1 rounded border border-slate-300 bg-white font-semibold text-slate-700 text-[12px]">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      <ConsumerReviewDrawer
        review={selectedReview}
        mode={drawerMode}
        isOpen={drawerOpen}
        onClose={closeDrawer}
        onReviewUpdated={() => {
          fetchReviews();
          showToast('Review updated successfully');
        }}
      />

      {/* ── Toast ── */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </ConsumerLayout>
  );
};

export default ConsumerReviewsPage;
